import { z } from 'zod';
import { apiSchemas } from '@verifime/api-definition';
import { AdverseMediaEditor as AdverseMediaEditorComponent } from '@verifime/components';
import { useAdverseMediaData } from './hooks/useAdverseMediaData';
import { CircularProgress, Typography } from '@mui/material';

// Infer types from the auto-generated Zod schemas
type TAdverseMedia = z.infer<typeof apiSchemas.AdverseMedia>;
type TCreateAdverseMediaRequest = z.infer<typeof apiSchemas.CreateAdverseMediaRequest>;

export type TAdverseMediaViewProps = Parameters<typeof useAdverseMediaData>[0];

/**
 * The `AdverseMediaEditor` view component combines the `AdverseMediaEditor` component from the components package
 * with the `useAdverseMediaData` hook to provide a complete adverse media management interface.
 *
 * This component:
 * - Fetches existing adverse media data for a customer
 * - Displays loading states during data retrieval
 * - Allows users to add new adverse media records
 * - Handles form submission and API communication
 */
export default function AdverseMediaEditor(props: TAdverseMediaViewProps) {
  const { history, isLoading, error, createRecord } = useAdverseMediaData(props);

  const handleSubmit = async (formData: TAdverseMedia) => {
    // Map the form data to the shape expected by the API.
    const apiPayload: TCreateAdverseMediaRequest = {
      hasAdverseMedia: formData.adverseMediaStatus,
      comment: formData.commentText,
    };
    await createRecord(apiPayload);
  };

  if (isLoading) {
    return (
      <Typography variant="body1">
        <CircularProgress /> Loading Adverse Media...
      </Typography>
    );
  }

  return (
    <>
      {error && <Typography color="error">Failed to load adverse media information.</Typography>}
      <AdverseMediaEditorComponent data={history} onSubmitNewAdverseMediaStatus={handleSubmit} />
    </>
  );
}
